<template>
  <div class="sxContent productsCont normalTop accessory-info-wrap">
    <div class="productsTit padding-10 flex justify-between">
      <div>附件信息</div>
      <div class="accessory-header-actions">
        <Button type="primary" size="small" icon="ios-cloud-download-outline" :disabled="!accessoryList.length || batchDownloadLoading" @click="toggleBatchMode">
          批量下载
        </Button>
        <Button v-if="!isDisabled" type="primary" size="small" icon="md-add" @click="openAddModal">添加附件</Button>
      </div>
    </div>
    <div class="products-content padding-10">
      <Table :columns="tableColumns" :data="accessoryList" border size="small" class="accessory-table" :no-data-text="'暂无已上传的附件文档'">
        <template slot-scope="{ row }" slot="batchSelection">
          <Checkbox :value="selectedAccessoryIds.includes(String(row.id))" :disabled="batchDownloadLoading" @on-change="toggleAccessorySelection(row, $event)" />
        </template>
        <template slot-scope="{ row }" slot="fileName">
          <span class="file-name-link" @click="handlePreview(row)">{{ row.fileName }}</span>
        </template>
        <template slot-scope="{ row, index }" slot="action">
          <template v-if="!isDisabled">
            <Button type="text" class="action-btn btn-edit" @click="openEditModal(index)">编辑</Button>
            <Button type="text" class="action-btn btn-delete" @click="handleDelete(index)">删除</Button>
          </template>
          <Button type="text" class="action-btn btn-download" @click="handleDownload(row)">下载</Button>
        </template>
      </Table>
      <div v-if="isBatchMode" class="batch-download-footer flex justify-between">
        <div>已选择 <span class="batch-selected-count">{{ selectedAccessoryIds.length }}</span> 项附件</div>
        <div>
          <Button size="small" :disabled="batchDownloadLoading" @click="toggleBatchMode">取消</Button>
          <Button type="primary" size="small" :loading="batchDownloadLoading" @click="handleBatchDownload">确认下载</Button>
        </div>
      </div>
      <!-- <div v-if="isEmptyList" class="empty-tip">暂无已上传的附件文档</div> -->
    </div>

    <!-- 添加/编辑附件弹窗 -->
    <Modal v-model="modalVisible" :title="modalTitle" :mask-closable="false" :transfer="true" width="600" class="accessory-modal">
      <Form ref="accessoryForm" :model="formData" label-position="top">
        <FormItem label="文档分类">
          <div class="category-select-wrapper">
            <Select v-model="formData.typeId" placeholder="请选择文档分类">
              <Option v-for="item in typeList" :value="item.id" :key="`category-${item.id}`">{{ item.typeName }}</Option>
            </Select>
            <Button @click="toggleCustomCategory" class="btn-toggle-custom">
              {{ isShowCustomCategory ? "取消添加" : "添加文档分类" }}
            </Button>
          </div>
          <div v-if="isShowCustomCategory" class="custom-category-group">
            <Input ref="newCategoryInput" v-model="newCategoryName" placeholder="输入新分类名称 (如: ISO9001)" maxlength="50" />
            <Button type="success" @click="handleAddCategory" class="btn-add-category">新增</Button>
          </div>
        </FormItem>
        <FormItem :label="modalType === 'add' ? '选择附件' : '重新上传覆盖'">
          <dytUpload
            ref="accessoryUploadRef"
            class="accessory-upload"
            :action="fileUploadUrl"
            :multiple="modalType === 'add'"
            accept=".pdf, .jpg, .png, .jpeg"
            :format="['pdf', 'jpg', 'png', 'jpeg']"
            :max-size="fileMaxSize"
            :before-upload="beforeUpload"
            :on-format-error="handleFormatError"
            :on-exceeded-size="handleExceededSize"
            :show-upload-list="false"
          >
            <div class="file-dropzone">
              <div class="dropzone-text">点击选择文件或拖拽文件到这里</div>
            </div>
          </dytUpload>
          <div v-if="!$common.isEmpty(formData.fileList)" class="selected-file-list">
            <div v-for="(file, fIndex) in formData.fileList" :key="`selected-${fIndex}`" class="selected-file-item">
              <Icon type="md-document" class="selected-file-icon" />
              <span class="selected-file-name">{{ file.fileName }}</span>
              <Icon type="md-close" class="selected-file-remove" @click="removeSelectedFile(fIndex)" />
            </div>
          </div>
        </FormItem>
      </Form>
      <div slot="footer">
        <Button @click="cancelModal">取消</Button>
        <Button type="primary" @click="saveAccessory" :loading="saveLoading">保存提交</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import api from "@/api/api";

/**
 * @description 商品附件信息管理组件
 * @author 前端开发
 * @功能说明
 *   1. 附件展示：以单个附件为维度展示附件类型、附件名称、操作（productAttachmentList）
 *   2. 附件上传：添加模式支持一次上传多个 PDF 文件（productAttachmentUploadBatch，直接提交 file 对象）
 *   3. 附件编辑：可修改文档分类；重新上传时先传文件服务器拿路径，再连同文件名提交更新（productAttachmentUpdate）
 *   4. 附件下载/预览：查看和编辑模式下均可下载；预览支持 pdf 与图片（jpg/jpeg/png）
 *   5. 附件删除：编辑模式下删除需二次确认（productAttachmentDelete）
 */
export default {
  name: "productAccessoryInfo",
  props: {
    /** 是否禁用编辑（查看模式） */
    isDisabled: {
      type: Boolean,
      default: false,
    },
    /** 商品ID */
    productId: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      /** 附件列表（接口返回：{id, productId, fileName, filePath, typeId}） */
      accessoryList: [],
      /** 批量下载模式及选中的附件ID */
      isBatchMode: false,
      selectedAccessoryIds: [],
      batchDownloadLoading: false,
      /** 文档分类（附件类型）列表：[{id, typeName}] */
      typeList: [],
      /** 弹窗显示状态 */
      modalVisible: false,
      /** 弹窗类型 add-添加 edit-编辑 */
      modalType: "add",
      /** 当前编辑的附件索引 */
      editIndex: -1,
      /** 弹窗表单数据 */
      formData: {
        typeId: "",
        fileList: [],
      },
      /** 是否显示自定义分类输入 */
      isShowCustomCategory: false,
      /** 新分类名称 */
      newCategoryName: "",
      /** 保存loading */
      saveLoading: false,
      /** 文件服务器上传接口（编辑附件重新上传文件时使用） */
      fileUploadUrl: api.upload_files + "?basePath=/product",
      /** 文件大小限制（kb） */
      fileMaxSize: 60 * 1024,
    };
  },
  computed: {
    /** 弹窗标题 */
    modalTitle() {
      return this.modalType === "add" ? "添加附件" : "编辑附件分类/文件";
    },
    /** 表格列配置 */
    tableColumns() {
      const columns = [
        {
          title: "附件类型",
          key: "typeName",
          align: "center",
          minWidth: 120,
          render: (h, params) => {
            const typeItem = this.typeList.find((item) => item.id === params.row.typeId);
            return h("span", typeItem ? typeItem.typeName : "");
          },
        },
        {
          title: "附件名称",
          slot: "fileName",
          align: "center",
          minWidth: 200,
        },
        {
          title: "操作",
          slot: "action",
          align: "center",
          width: 220,
        },
      ];
      if (this.isBatchMode) {
        columns.unshift({
          slot: "batchSelection",
          width: 50,
          align: "center",
          renderHeader: (h) => h("Checkbox", {
            props: {
              value: this.accessoryList.length > 0 && this.selectedAccessoryIds.length === this.accessoryList.length,
              indeterminate: this.selectedAccessoryIds.length > 0 && this.selectedAccessoryIds.length < this.accessoryList.length,
              disabled: this.batchDownloadLoading,
            },
            on: { "on-change": this.toggleSelectAllAccessories },
          }),
        });
      }
      return columns;
    },
    // /** 附件列表是否为空 */
    // isEmptyList() {
    //   return this.$common.isEmpty(this.accessoryList);
    // }
  },
  watch: {
    /** 商品ID变化时重新拉取附件列表 */
    productId: {
      immediate: true,
      handler(val) {
        this.isBatchMode = false;
        this.selectedAccessoryIds = [];
        if (this.$common.isEmpty(val)) {
          this.accessoryList = [];
          return;
        }
        this.getAccessoryList();
      },
    },
  },
  created() {
    this.getTypeList();
  },
  methods: {
    /** 切换批量下载模式，退出时清空选择 */
    toggleBatchMode() {
      if (this.batchDownloadLoading) return;
      this.isBatchMode = !this.isBatchMode;
      this.selectedAccessoryIds = [];
    },
    toggleAccessorySelection(row, checked) {
      const id = String(row.id);
      this.selectedAccessoryIds = this.selectedAccessoryIds.filter((item) => item !== id);
      if (checked) this.selectedAccessoryIds.push(id);
    },
    toggleSelectAllAccessories(checked) {
      this.selectedAccessoryIds = checked ? this.accessoryList.map((item) => String(item.id)) : [];
    },
    /** 提交附件ID数组，下载接口返回的文件流 */
    async handleBatchDownload() {
      if (this.batchDownloadLoading) return;
      if (!this.selectedAccessoryIds.length) {
        this.$Message.warning("请先勾选需要下载的文件！");
        return;
      }
      this.batchDownloadLoading = true;
      try {
        const res = await this.axios.post(api.productAttachmentDownloadBatch, this.selectedAccessoryIds.slice(), {
          responseType: "blob",
          isCache: false,
        });
        const fileBlob = res && (res.resData || res.data);
        if (!(fileBlob instanceof Blob) || !fileBlob.size || /json|text\/html/i.test(fileBlob.type)) {
          this.$Message.error("批量下载失败");
          return;
        }
        await this.$common.downloadFile(fileBlob, { name: res.filename || "附件批量下载" });
        this.selectedAccessoryIds = [];
        this.isBatchMode = false;
      } catch (error) {
        this.$Message.error("批量下载失败");
      } finally {
        this.batchDownloadLoading = false;
      }
    },
    /**
     * 获取文档分类（附件类型）列表
     */
    getTypeList() {
      this.axios.get(api.productAttachmentTypeList).then((res) => {
        if (!res || !res.data || res.data.code !== 0) return;
        this.typeList = res.data.datas || [];
        // 类型名称加载完成后重新排序，确保同一附件类型连续展示
        this.sortAccessoryList();
      });
    },
    /**
     * 获取附件列表
     */
    getAccessoryList() {
      this.axios.get(api.productAttachmentList, { params: { productId: this.productId } }).then((res) => {
        if (!res || !res.data || res.data.code !== 0) return;
        this.accessoryList = res.data.datas || [];
        this.selectedAccessoryIds = this.selectedAccessoryIds.filter((id) => this.accessoryList.some((item) => String(item.id) === id));
        this.sortAccessoryList();
      });
    },
    /** 按附件类型名称排序，保证同一类型的附件集中展示 */
    sortAccessoryList() {
      const typeNameMap = this.typeList.reduce((map, item) => {
        map[item.id] = item.typeName || "";
        return map;
      }, {});
      this.accessoryList.sort((a, b) => {
        const nameA = typeNameMap[a.typeId] || a.typeName || "";
        const nameB = typeNameMap[b.typeId] || b.typeName || "";
        return nameA.localeCompare(nameB, "zh-CN");
      });
    },
    /**
     * 打开添加附件弹窗
     */
    openAddModal() {
      this.modalType = "add";
      this.editIndex = -1;
      this.resetForm();
      this.modalVisible = true;
    },
    /**
     * 打开编辑附件弹窗
     * @param {Number} index 附件索引
     */
    openEditModal(index) {
      const item = this.accessoryList[index];
      if (!item) return;
      this.modalType = "edit";
      this.editIndex = index;
      this.resetForm();
      this.formData.typeId = item.typeId || "";
      // 回显原文件（无 file 字段表示未重新上传）
      this.formData.fileList = [
        {
          fileName: item.fileName,
          filePath: item.filePath,
        },
      ];
      this.modalVisible = true;
    },
    /**
     * 重置弹窗表单
     */
    resetForm() {
      this.formData = {
        typeId: "",
        fileList: [],
      };
      this.isShowCustomCategory = false;
      this.newCategoryName = "";
      this.saveLoading = false;
    },
    /**
     * 切换自定义分类输入显示
     */
    toggleCustomCategory() {
      this.isShowCustomCategory = !this.isShowCustomCategory;
      if (this.isShowCustomCategory) {
        this.$nextTick(() => {
          const inputRef = this.$refs.newCategoryInput;
          if (inputRef && inputRef.focus) {
            inputRef.focus();
          }
        });
      }
    },
    /**
     * 添加新文档分类（全局公用，仅需提交类型名称）
     */
    handleAddCategory() {
      const name = this.newCategoryName.trim();
      if (!name) {
        this.$Message.error("请输入新分类名称！");
        return;
      }
      this.axios.post(api.productAttachmentTypeSave, { typeName: name }).then((res) => {
        if (!res || !res.data || res.data.code !== 0) return;
        this.$Message.success("添加文档分类成功");
        this.newCategoryName = "";
        this.isShowCustomCategory = false;
        // 重新拉取类型列表并选中新添加的分类
        this.axios.get(api.productAttachmentTypeList).then((listRes) => {
          if (!listRes || !listRes.data || listRes.data.code !== 0) return;
          this.typeList = listRes.data.datas || [];
          this.sortAccessoryList();
          const typeItem = this.typeList.find((item) => item.typeName === name);
          if (typeItem) {
            this.formData.typeId = typeItem.id;
          }
        });
      });
    },
    /**
     * 文件选择后仅暂存 File 对象（保存提交时统一上传）
     * @param {File} file 文件对象
     * @returns {Boolean} 阻止组件自动上传
     */
    beforeUpload(file) {
      if (this.modalType === "add") {
        this.formData.fileList.push({ file: file, fileName: file.name });
      } else {
        this.formData.fileList = [{ file: file, fileName: file.name }];
      }
      return false;
    },
    /**
     * 文件格式错误回调
     */
    handleFormatError() {
      this.$Message.error("文件格式不对，请上传格式为 pdf、jpg、png、jpeg 的文件");
    },
    /**
     * 文件超出大小限制回调
     */
    handleExceededSize() {
      this.$Message.error("文件过大，请上传60M以内的文件");
    },
    /**
     * 上传文件到文件服务器（编辑附件重新上传文件时使用）
     * @param {File} file 文件对象
     * @returns {Promise} resolve(文件路径)
     */
    uploadFileToServer(file) {
      const formData = new FormData();
      formData.append("files", file);
      return this.axios.post(this.fileUploadUrl, formData, { isCache: false }).then((res) => {
        if (!res || !res.data || res.data.code !== 0 || this.$common.isEmpty(res.data.datas)) {
          return Promise.reject(new Error("文件上传失败"));
        }
        return res.data.datas[0];
      });
    },
    /**
     * 新增附件（批量上传 file 对象，由后端存储并创建附件记录）
     * @returns {Promise}
     */
    addAccessory() {
      const formData = new FormData();
      this.formData.fileList.forEach((item) => {
        formData.append("files", item.file);
      });
      const url = `${api.productAttachmentUploadBatch}${this.productId}/${this.formData.typeId}`;
      return this.axios
        .post(url, formData, { isCache: false })
        .then((res) => {
          if (!res || !res.data || res.data.code !== 0) {
            this.$Message.error("添加附件失败");
            return false;
          }
          this.$Message.success("添加附件成功");
          return true;
        })
        .catch(() => {
          this.$Message.error("添加附件失败");
          return false;
        });
    },
    /**
     * 编辑附件（重新上传文件时先上传文件服务器拿路径，再连同文件名提交更新）
     * @returns {Promise}
     */
    editAccessory() {
      const currentItem = this.formData.fileList[0];
      const editRow = this.accessoryList[this.editIndex] || {};
      // 有新文件对象时先上传文件服务器获取路径，否则沿用原路径
      const uploadHandler = currentItem.file ? this.uploadFileToServer(currentItem.file) : Promise.resolve(currentItem.filePath);
      return uploadHandler
        .then((filePath) => {
          const params = {
            id: editRow.id,
            productId: this.productId,
            fileName: currentItem.file ? currentItem.file.name : currentItem.fileName,
            filePath: filePath,
            typeId: this.formData.typeId,
          };
          return this.axios
            .put(api.productAttachmentUpdate, params)
            .then((res) => {
              if (!res || !res.data || res.data.code !== 0) {
                this.$Message.error("编辑附件失败");
                return false;
              }
              this.$Message.success("编辑附件成功");
              return true;
            })
            .catch(() => {
              this.$Message.error("编辑附件失败");
              return false;
            });
        })
        .catch(() => {
          this.$Message.error("文件上传失败");
          return false;
        });
    },
    /**
     * 保存附件
     */
    async saveAccessory() {
      if (this.$common.isEmpty(this.formData.typeId)) {
        this.$Message.error("请选择文档分类！");
        return;
      }
      if (this.$common.isEmpty(this.formData.fileList)) {
        this.$Message.error("请选择需要上传的文件！");
        return;
      }
      this.saveLoading = true;
      const isSuccess = this.modalType === "add" ? await this.addAccessory() : await this.editAccessory();
      this.saveLoading = false;
      if (!isSuccess) return;
      this.modalVisible = false;
      this.getAccessoryList();
    },
    /**
     * 移除已选中的上传文件
     * @param {Number} index 文件索引
     */
    removeSelectedFile(index) {
      this.formData.fileList.splice(index, 1);
    },
    /**
     * 取消弹窗
     */
    cancelModal() {
      this.modalVisible = false;
    },
    /**
     * 删除附件（二次确认）
     * @param {Number} index 附件索引
     */
    handleDelete(index) {
      const item = this.accessoryList[index];
      if (!item) return;
      this.$Modal.confirm({
        title: "操作确认",
        content: "<p>确定要删除该附件吗？</p>",
        onOk: () => {
          this.axios.delete(api.productAttachmentDelete + "?id=" + item.id).then((res) => {
            if (!res || !res.data || res.data.code !== 0) return;
            this.$Message.success("删除附件成功");
            this.getAccessoryList();
          });
        },
      });
    },
    /**
     * 下载附件
     * @param {Object} row 附件数据
     */
    handleDownload(row) {
      if (this.$common.isEmpty(row.filePath)) return;
      const fileUrl = `${window.location.origin}/product-service/filenode/s${row.filePath}`;
      this.$common.downloadFile(fileUrl, { name: row.fileName });
    },
    /**
     * 预览附件（支持 pdf 与图片类型 jpg/jpeg/png）
     * @param {Object} row 附件数据
     */
    handlePreview(row) {
      if (this.$common.isEmpty(row.filePath)) return;
      const fileUrl = `${window.location.origin}/product-service/filenode/s${row.filePath}`;
      // 根据文件后缀获取对应的 MIME 类型
      const fileName = row.fileName || row.filePath;
      const suffix = (fileName.substring(fileName.lastIndexOf(".") + 1) || "").toLocaleLowerCase();
      const mimeMap = {
        pdf: "application/pdf",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
      };
      const mimeType = mimeMap[suffix];
      // 未知类型直接新窗口打开
      if (this.$common.isEmpty(mimeType)) {
        window.open(fileUrl);
        return;
      }
      this.axios
        .get(fileUrl, { responseType: "blob" })
        .then((res) => {
          this.$common.blobToBase64(res.data || res.resData).then((data) => {
            const fileBlob = this.$common.base64ToBlob(data, mimeType);
            this.$common.previewFile(fileBlob);
          });
        })
        .catch(() => {
          this.$Message.error("文件预览失败");
        });
    },
  },
};
</script>

<style lang="less">
.accessory-info-wrap {
  .accessory-header-actions {
    display: flex;
    gap: 8px;
  }
  .batch-download-footer {
    align-items: center;
    padding: 10px;
    background-color: #f5f5f5;
    border: 1px solid #e8eaec;
    border-top: none;
    .ivu-btn + .ivu-btn {
      margin-left: 8px;
    }
  }
  .batch-selected-count {
    color: #2d8cf0;
    font-weight: bold;
  }
  .flex {
    display: flex;
  }
  .justify-between {
    justify-content: space-between;
  }
  // .accessory-table {
  //   // margin-top: 5px;
  // }
  .empty-tip {
    text-align: center;
    color: #909399;
    padding: 20px 0;
    font-size: 13px;
  }
  .file-name-link {
    color: #1890ff;
    text-decoration: underline;
    cursor: pointer;
    &:hover {
      color: #40a9ff;
    }
  }
  .action-btn {
    padding: 0 8px;
    &.btn-edit {
      color: #1890ff;
      &:hover {
        color: #40a9ff;
      }
    }
    &.btn-download {
      color: #52c41a;
      &:hover {
        color: #73d13d;
      }
    }
    &.btn-delete {
      color: #ff4d4f;
      &:hover {
        color: #ff7875;
      }
    }
  }
}
.accessory-modal {
  .category-select-wrapper {
    display: flex;
    gap: 8px;

    .ivu-select {
      flex: 1;
    }

    .btn-toggle-custom {
      white-space: nowrap;
    }
  }

  .custom-category-group {
    display: flex;
    gap: 8px;
    margin-top: 8px;

    .ivu-input-wrapper {
      flex: 1;
    }

    .btn-add-category {
      white-space: nowrap;
    }
  }

  .accessory-upload {
    display: block;
    .ivu-upload-select {
      display: block;
    }
    .file-dropzone {
      position: relative;
      border: 1px dashed #dcdfe6;
      border-radius: 4px;
      padding: 20px 10px;
      text-align: center;
      cursor: pointer;
      background-color: #fafafa;
      transition: all 0.2s;

      &:hover {
        border-color: #40a9ff;
        background-color: #f0f7ff;
      }

      .dropzone-text {
        font-size: 13px;
        color: #909399;
      }
    }
  }

  .selected-file-list {
    margin-top: 8px;

    .selected-file-item {
      display: flex;
      align-items: center;
      width: 100%;
      height: 28px;
      line-height: 28px;
      padding: 0 8px;
      margin-bottom: 4px;
      background-color: transparent;
      border-radius: 2px;

      &:hover {
        background-color: #f5f5f5;
        .selected-file-remove {
          visibility: visible;
        }
      }

      .selected-file-icon {
        flex-shrink: 0;
        margin-right: 5px;
        font-size: 15px;
        color: #606266;
      }

      .selected-file-name {
        flex: 1;
        min-width: 0;
        padding-right: 8px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 13px;
        color: #2d8cf0;
      }

      .selected-file-remove {
        visibility: hidden;
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        line-height: 16px;
        text-align: center;
        font-size: 14px;
        color: #c0c4cc;
        cursor: pointer;

        &:hover {
          color: #909399;
        }
      }
    }
  }
}
</style>
